import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost } from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  ArrowRight, ArrowUpRight, Loader2, CheckCircle2, Users, Zap,
  Send, ChevronLeft, ChevronRight, RotateCcw,
  Star, Target, AlertTriangle, Shield,
} from 'lucide-react';
import { ExecutiveReport } from './ExecutiveReport';

const STANCE_BADGE = { support: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20', caution: 'bg-amber-500/10 text-amber-600 border-amber-500/20', oppose: 'bg-red-500/10 text-red-600 border-red-500/20', neutral: 'bg-gray-500/10 text-gray-600 border-gray-500/20' };

export function MeetingRoom({ onBack, onReport }) {
  const [topic, setTopic] = useState('');
  const [meeting, setMeeting] = useState(null);
  const [meetingId, setMeetingId] = useState(null);
  const [polling, setPolling] = useState(false);
  const [followUp, setFollowUp] = useState('');
  const [pastMeetings, setPastMeetings] = useState([]);
  const endRef = useRef(null);

  useEffect(() => { apiGet('/advisory/meetings').then(r => r.ok ? r.json() : []).then(setPastMeetings).catch(() => {}); }, [meeting?.status]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [meeting?.rounds]);

  const pollJob = async (jobId, onDone) => {
    setPolling(true);
    for (let i = 0; i < 60; i++) {
      await new Promise(r => setTimeout(r, 2000));
      try {
        const res = await apiGet(`/advisory/meeting-job/${jobId}`);
        if (res.ok) {
          const job = await res.json();
          if (job.status === 'complete') { onDone(job); setPolling(false); return; }
          if (job.status === 'failed') { toast.error(job.error || 'Failed'); setPolling(false); return; }
        }
      } catch { /* retry */ }
    }
    toast.error('Timed out'); setPolling(false);
  };

  const loadMeeting = async (id) => {
    const res = await apiGet(`/advisory/meetings/${id}`);
    if (res.ok) { const data = await res.json(); setMeeting(data); setMeetingId(id); }
  };

  const startMeeting = async () => {
    if (!topic.trim()) return;
    setPolling(true);
    try {
      const res = await apiPost('/advisory/meetings', { topic: topic.trim() });
      if (res.ok) {
        const { meeting_id, job_id } = await res.json();
        setMeetingId(meeting_id);
        await pollJob(job_id, async () => { await loadMeeting(meeting_id); });
      } else toast.error('Failed to start meeting');
    } catch { toast.error('Connection error'); }
    setPolling(false);
  };

  const continueDiscussion = async (requestDebate = false) => {
    if (!meetingId) return;
    setPolling(true);
    try {
      const res = await apiPost(`/advisory/meetings/${meetingId}/continue`, { user_input: followUp.trim() || null, request_debate: requestDebate });
      if (res.ok) {
        const { job_id } = await res.json();
        setFollowUp('');
        await pollJob(job_id, async () => { await loadMeeting(meetingId); });
      } else toast.error('Failed');
    } catch { toast.error('Connection error'); }
    setPolling(false);
  };

  const concludeMeeting = async () => {
    if (!meetingId) return;
    setPolling(true);
    try {
      const res = await apiPost(`/advisory/meetings/${meetingId}/conclude`, {});
      if (res.ok) {
        const { job_id } = await res.json();
        await pollJob(job_id, async () => {
          await loadMeeting(meetingId);
          const updated = await apiGet(`/advisory/meetings/${meetingId}`);
          if (updated.ok) { const m = await updated.json(); if (m.executive_report) onReport(m); }
        });
      } else toast.error('Failed');
    } catch { toast.error('Connection error'); }
    setPolling(false);
  };

  if (!meeting) return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="meeting-topic">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6"><ChevronLeft className="w-4 h-4" /> Back</button>
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-2xl bg-violet-500/10 flex items-center justify-center mx-auto mb-4"><Users className="w-8 h-8 text-violet-500" /></div>
        <h1 className="text-2xl font-bold mb-2">Start a Board Meeting</h1>
        <p className="text-muted-foreground text-sm">What strategic topic should your Advisory Board discuss?</p>
      </div>
      <div className="space-y-4">
        <textarea value={topic} onChange={e => setTopic(e.target.value)} placeholder="e.g., Should we expand into the European market this quarter, or double down on our US growth?" className="w-full min-h-[100px] p-4 rounded-xl border border-border bg-background text-base resize-none focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-500 text-left" dir="ltr" data-testid="meeting-topic-input" />
        <Button onClick={startMeeting} disabled={!topic.trim() || polling} className="w-full bg-violet-600 hover:bg-violet-700 text-white py-3 text-base" data-testid="start-meeting-btn">
          {polling ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Convening the board...</> : <>Start Board Meeting <ArrowRight className="w-4 h-4 ml-2" /></>}
        </Button>
      </div>
      {pastMeetings.length > 0 && (
        <div className="mt-8">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Previous Meetings</h3>
          <div className="space-y-2">{pastMeetings.map(m => (
            <button key={m.meeting_id} onClick={() => loadMeeting(m.meeting_id)} className="w-full flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors text-left" data-testid={`past-meeting-${m.meeting_id}`}>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${m.status === 'concluded' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-violet-500/10 text-violet-500'}`}>{m.status === 'concluded' ? <CheckCircle2 className="w-4 h-4" /> : <Users className="w-4 h-4" />}</div>
              <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{m.topic}</p><p className="text-xs text-muted-foreground">{m.round_count} rounds &middot; {new Date(m.created_at).toLocaleDateString()}</p></div>
              <span className={`text-xs px-2 py-0.5 rounded-full ${m.status === 'concluded' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400'}`}>{m.status === 'concluded' ? 'Report Ready' : 'Active'}</span>
              <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
            </button>
          ))}</div>
        </div>
      )}
    </div>
  );

  if (meeting.status === 'concluded' && meeting.executive_report) {
    return <ExecutiveReport report={meeting.executive_report} meeting={meeting} onBack={() => { setMeeting(null); setMeetingId(null); }} />;
  }

  const advisorMap = {};
  (meeting.advisors || []).forEach(a => { advisorMap[a.id] = a; });

  return (
    <div className="max-w-4xl mx-auto px-4 py-6" data-testid="meeting-room">
      <button onClick={() => { setMeeting(null); setMeetingId(null); }} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"><ChevronLeft className="w-4 h-4" /> Back to meetings</button>
      <div className="text-center mb-6"><h1 className="text-xl font-bold mb-1">{meeting.topic}</h1><p className="text-xs text-muted-foreground">{meeting.rounds?.length || 0} discussion round(s)</p></div>
      <div className="flex justify-center gap-2 mb-6">
        {(meeting.advisors || []).map(a => (
          <div key={a.id} className="flex flex-col items-center gap-1" title={a.name}>
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: a.avatar_color }}>{a.name.split(' ').map(n => n[0]).join('')}</div>
            <span className="text-[9px] text-muted-foreground max-w-[60px] truncate">{a.name.split(' ')[1]}</span>
          </div>
        ))}
      </div>
      <div className="space-y-6 mb-6">
        {(meeting.rounds || []).map((round, ri) => (
          <div key={ri} className="space-y-3">
            <div className="flex items-center gap-2"><div className="h-px flex-1 bg-border" /><span className="text-xs font-semibold text-muted-foreground uppercase px-2">Round {round.round_num} &middot; {round.type}</span><div className="h-px flex-1 bg-border" /></div>
            {round.moderator_note && <p className="text-xs text-center text-muted-foreground italic">{round.moderator_note}</p>}
            {round.user_input && <div className="flex justify-end"><div className="bg-odapto-orange text-white rounded-2xl rounded-br-sm px-4 py-2 max-w-[70%] text-sm">{round.user_input}</div></div>}
            {(round.responses || []).map((resp, i) => {
              const adv = advisorMap[resp.advisor_id] || {};
              const stanceClass = STANCE_BADGE[resp.stance] || STANCE_BADGE.neutral;
              return (
                <div key={i} className="flex gap-3" data-testid={`response-${resp.advisor_id}-r${round.round_num}`}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0 mt-1" style={{ backgroundColor: adv.avatar_color || '#666' }}>{(resp.advisor_name || '?').split(' ').map(n => n[0]).join('')}</div>
                  <div className="flex-1 min-w-0 bg-muted rounded-2xl rounded-tl-sm px-4 py-3">
                    <div className="flex items-center gap-2 mb-1.5"><span className="text-xs font-semibold">{resp.advisor_name}</span><span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${stanceClass}`}>{resp.stance}</span>{resp.confidence && <span className="text-[10px] text-muted-foreground ml-auto">{resp.confidence}% confident</span>}</div>
                    <p className="text-sm leading-relaxed">{resp.content}</p>
                    {resp.key_point && <p className="text-xs text-muted-foreground mt-1 italic border-t border-border/50 pt-1">{resp.key_point}</p>}
                  </div>
                </div>
              );
            })}
            {(round.agreements?.length > 0 || round.disagreements?.length > 0) && (
              <div className="grid gap-2 sm:grid-cols-2 mx-8">
                {round.agreements?.length > 0 && <div className="p-2 rounded-lg bg-emerald-500/5 border border-emerald-500/20"><p className="text-[10px] font-semibold text-emerald-600 uppercase mb-1">Agreements</p>{round.agreements.map((a, i) => <p key={i} className="text-xs text-emerald-700 dark:text-emerald-300">{a}</p>)}</div>}
                {round.disagreements?.length > 0 && <div className="p-2 rounded-lg bg-red-500/5 border border-red-500/20"><p className="text-[10px] font-semibold text-red-600 uppercase mb-1">Disagreements</p>{round.disagreements.map((d, i) => <p key={i} className="text-xs text-red-700 dark:text-red-300">{d}</p>)}</div>}
              </div>
            )}
          </div>
        ))}
        {polling && <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-violet-500" /><span className="text-sm text-muted-foreground ml-2">Advisors are discussing...</span></div>}
        <div ref={endRef} />
      </div>
      {!polling && meeting.status === 'active' && (
        <div className="border-t border-border pt-4 space-y-3">
          <div className="flex gap-2">
            <Input value={followUp} onChange={e => setFollowUp(e.target.value)} placeholder="Ask a follow-up question (optional)..." dir="ltr" className="flex-1" onKeyDown={e => e.key === 'Enter' && continueDiscussion()} data-testid="meeting-followup-input" />
            <Button onClick={() => continueDiscussion(false)} className="bg-violet-600 hover:bg-violet-700 text-white shrink-0" data-testid="continue-discussion-btn"><Send className="w-4 h-4" /></Button>
          </div>
          <div className="flex justify-center gap-3">
            <Button onClick={() => continueDiscussion(true)} variant="outline" className="text-xs border-red-500/30 text-red-600 hover:bg-red-500/5" data-testid="request-debate-btn"><Zap className="w-3 h-3 mr-1" /> Request Debate</Button>
            <Button onClick={concludeMeeting} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" data-testid="conclude-meeting-btn"><CheckCircle2 className="w-3 h-3 mr-1" /> Conclude &amp; Generate Report</Button>
          </div>
        </div>
      )}
    </div>
  );
}
